import { Star } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

// Read-only stars (supports halves visually by rounding to the nearest whole star).
export const Stars = ({ value = 0, size = "h-4 w-4", testId }) => {
  const full = Math.round(value);
  return (
    <span dir="ltr" className="inline-flex shrink-0 items-center gap-0.5" aria-label={`${value}/5`} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= full ? "fill-[var(--gold)] text-[var(--gold)]" : "text-neutral-600"}`} />
      ))}
    </span>
  );
};

export const StarInput = ({ value, onChange, testId = "review-stars-input" }) => {
  const { t } = useI18n();
  return (
    <div dir="ltr" className="flex items-center gap-1" role="radiogroup" aria-label={t("reviews.stars")} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" role="radio" aria-checked={value === i} aria-label={t("reviews.starsN", { n: i })}
          onClick={() => onChange(i)} className="rounded-md p-1 transition-transform duration-150 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold)]"
          data-testid={`${testId}-${i}`}>
          <Star className={`h-7 w-7 ${i <= (value || 0) ? "fill-[var(--gold)] text-[var(--gold)]" : "text-neutral-500"}`} />
        </button>
      ))}
    </div>
  );
};

// "★★★★☆ 4,3 · 12 avis" on cards and detail; "Aucun avis" when empty.
export const RatingBadge = ({ rating, testId }) => {
  const { t, locale } = useI18n();
  const count = rating?.count || 0;
  if (!count) return <span className="text-xs text-neutral-500" data-testid={testId}>{t("reviews.none")}</span>;
  const avg = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rating.avg);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs" data-testid={testId}>
      <Stars value={rating.avg} size="h-3.5 w-3.5" />
      <span className="font-semibold text-[var(--gold-bright)]" data-testid={testId && `${testId}-avg`}>{avg}</span>
      <span className="text-neutral-400" data-testid={testId && `${testId}-count`}>· {t(count === 1 ? "reviews.count.one" : "reviews.count.other", { n: count })}</span>
    </span>
  );
};
